import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, MessageSquare, Send, User, Store, Shield, 
  Search, Plus, X, Loader2, Mail, Clock, Check, CheckCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

function Messages() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [otherParticipant, setOtherParticipant] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [messageableUsers, setMessageableUsers] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [newSubject, setNewSubject] = useState('');
  const [newContent, setNewContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadConversations();
      
      // Check if there's a recipient in URL params (for "Message Seller" links)
      const recipientId = searchParams.get('to');
      if (recipientId) {
        startNewConversation(recipientId);
      }
    }
  }, [currentUser, searchParams]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get('/auth/me');
      setCurrentUser(response.data);
    } catch {
      toast.error('Please log in to access messages');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get('/messages/conversations');
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await axios.get(`/messages/conversations/${conversationId}`);
      setMessages(response.data.messages);
      setOtherParticipant(response.data.other_participant);
      setSelectedConversation(response.data.conversation);
      
      // Refresh conversations to update unread count
      loadConversations();
    } catch (error) {
      toast.error('Error loading messages');
    }
  };

  const loadMessageableUsers = async () => {
    try {
      const response = await axios.get('/messages/users');
      setMessageableUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const startNewConversation = async (recipientId = null) => {
    try {
      const response = await axios.get('/messages/users');
      setMessageableUsers(response.data.users);
      setShowNewMessage(true);
      
      if (recipientId) {
        // Pre-select recipient if provided via URL param
        const recipient = response.data.users.find(u => u.user_id === recipientId);
        if (recipient) {
          setSelectedRecipient(recipient);
        } else {
          // If user not in messageable list, try to get their info directly
          try {
            const userResponse = await axios.get(`/users/${recipientId}`);
            if (userResponse.data) {
              setSelectedRecipient(userResponse.data);
            }
          } catch {
            // Fallback - just set the ID and let user fill in details
            toast.info('Please select a recipient');
          }
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Error loading recipients');
    }
  };

  const sendNewMessage = async () => {
    if (!selectedRecipient || !newSubject.trim() || !newContent.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      await axios.post('/messages', {
        recipient_id: selectedRecipient.user_id,
        subject: newSubject,
        content: newContent
      });
      
      toast.success('Message sent!');
      setShowNewMessage(false);
      setSelectedRecipient(null);
      setNewSubject('');
      setNewContent('');
      loadConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error sending message');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const response = await axios.post(`/messages/${selectedConversation.conversation_id}/reply`, {
        content: newMessage
      });
      
      setMessages([...messages, response.data.data]);
      setNewMessage('');
      loadConversations();
    } catch (error) {
      toast.error('Error sending reply');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return date.toLocaleDateString('en-US', { weekday: 'short' });
    return date.toLocaleDateString();
  };

  const getRoleBadge = (user) => {
    if (user?.is_admin) {
      return <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1"><Shield className="w-3 h-3" /> Admin</span>;
    }
    if (user?.role === 'creator') {
      return <span className="px-2 py-0.5 rounded-full text-xs bg-[#00F0FF]/20 text-[#00F0FF] flex items-center gap-1"><Store className="w-3 h-3" /> Seller</span>;
    }
    return <span className="px-2 py-0.5 rounded-full text-xs bg-[#00F0FF]/20 text-[#00F0FF] flex items-center gap-1"><User className="w-3 h-3" /> Buyer</span>;
  };

  const filteredUsers = messageableUsers.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#00F0FF] hover:text-[#00C2CC]"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Marketplace
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-[#00F0FF]" />
            <h1 className="text-xl font-bold">Messages</h1>
          </div>
          <Button
            onClick={() => startNewConversation()}
            className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Message
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-180px)]">
          {/* Conversations List */}
          <div className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden flex flex-col">
            <div className="p-4 border-b border-white/10">
              <h2 className="font-semibold mb-3">Conversations</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  placeholder="Search conversations..."
                  className="pl-10 bg-neutral-800 border-white/10"
                />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-8 text-center text-neutral-400">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No conversations yet</p>
                  <p className="text-sm mt-1">Start a new message to begin</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.conversation_id}
                    onClick={() => loadMessages(conv.conversation_id)}
                    className={`p-4 border-b border-white/5 cursor-pointer transition-colors hover:bg-white/5 ${
                      selectedConversation?.conversation_id === conv.conversation_id ? 'bg-white/10' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{conv.other_participant?.name || 'Unknown'}</span>
                        {conv.unread_count > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-[#00F0FF] text-black font-bold">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-neutral-500">
                        {formatDate(conv.updated_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      {getRoleBadge(conv.other_participant)}
                    </div>
                    <p className="text-sm text-neutral-400 truncate">
                      {conv.subject}
                    </p>
                    {conv.last_message && (
                      <p className="text-xs text-neutral-500 truncate mt-1">
                        {conv.last_message.content}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Message Thread */}
          <div className="lg:col-span-2 bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden flex flex-col">
            {selectedConversation ? (
              <>
                {/* Thread Header */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{otherParticipant?.name}</h3>
                      {getRoleBadge(otherParticipant)}
                    </div>
                    <p className="text-sm text-neutral-400">{selectedConversation.subject}</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_id === currentUser?.user_id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl p-4 ${
                          msg.sender_id === currentUser?.user_id
                            ? 'bg-[#00F0FF] text-black'
                            : 'bg-neutral-800 text-white'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <div className={`flex items-center gap-2 mt-2 text-xs ${
                          msg.sender_id === currentUser?.user_id ? 'text-black/60' : 'text-neutral-500'
                        }`}>
                          <Clock className="w-3 h-3" />
                          {formatDate(msg.created_at)}
                          {msg.sender_id === currentUser?.user_id && (
                            msg.read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Input */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      className="bg-neutral-800 border-white/10 resize-none"
                      rows={2}
                    />
                    <Button
                      onClick={sendReply}
                      disabled={sending || !newMessage.trim()}
                      className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black px-6"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-neutral-400">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Select a conversation or start a new message</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Message Modal */}
      {showNewMessage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-semibold">New Message</h3>
              <Button variant="ghost" size="sm" onClick={() => {
                setShowNewMessage(false);
                setSelectedRecipient(null);
              }}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Recipient Selection */}
              {!selectedRecipient ? (
                <div>
                  <label className="text-sm text-neutral-400 mb-2 block">Select Recipient</label>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search users..."
                      className="pl-10 bg-neutral-800 border-white/10"
                    />
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.user_id}
                        onClick={() => setSelectedRecipient(user)}
                        className="p-3 rounded-xl bg-neutral-800/50 hover:bg-neutral-800 cursor-pointer flex items-center justify-between"
                      >
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-neutral-400">{user.email}</p>
                        </div>
                        {getRoleBadge(user)}
                      </div>
                    ))}
                    {filteredUsers.length === 0 && (
                      <p className="text-center text-neutral-400 py-4">No users found</p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Selected Recipient */}
                  <div>
                    <label className="text-sm text-neutral-400 mb-2 block">To</label>
                    <div className="p-3 rounded-xl bg-neutral-800 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-medium">{selectedRecipient.name}</p>
                          <p className="text-sm text-neutral-400">{selectedRecipient.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getRoleBadge(selectedRecipient)}
                        <Button variant="ghost" size="sm" onClick={() => setSelectedRecipient(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="text-sm text-neutral-400 mb-2 block">Subject</label>
                    <Input
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      placeholder="What's this about?"
                      className="bg-neutral-800 border-white/10"
                    />
                  </div>

                  {/* Message Content */}
                  <div>
                    <label className="text-sm text-neutral-400 mb-2 block">Message</label>
                    <Textarea
                      value={newContent}
                      onChange={(e) => setNewContent(e.target.value)}
                      placeholder="Type your message..."
                      className="bg-neutral-800 border-white/10"
                      rows={5}
                    />
                  </div>
                </>
              )}
            </div>

            {selectedRecipient && (
              <div className="p-4 border-t border-white/10">
                <Button
                  onClick={sendNewMessage}
                  disabled={sending || !newSubject.trim() || !newContent.trim()}
                  className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Messages;
